import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useMembers } from '@/hooks/use-members'
import { useChekiOrdersByMember, type MemberChekiOrder } from '@/hooks/use-cheki'
import { formatDate } from '@/lib/utils'

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending:        { label: 'Pending',        className: 'bg-yellow-100 text-yellow-700' },
  proof_uploaded: { label: 'Bukti Terkirim', className: 'bg-blue-100 text-blue-700' },
  verified:       { label: 'Terverifikasi',  className: 'bg-green-100 text-green-700' },
  rejected:       { label: 'Ditolak',        className: 'bg-red-100 text-red-700' },
  cancelled:      { label: 'Dibatalkan',     className: 'bg-zinc-100 text-zinc-500' },
}

export default function MemberChekiPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { data: members = [] } = useMembers()
  const member = members.find((m) => m.id === id)
  const { data: orders = [], isLoading } = useChekiOrdersByMember(id!)

  const totalVerified = orders
    .filter((o) => o.status === 'verified')
    .reduce((sum, o) => sum + (o.quantity ?? 1), 0)

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/members')}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3">
          {member && (
            <div className="h-10 w-10 rounded-full overflow-hidden bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground shrink-0">
              {member.photo_url ? (
                <img
                  src={member.photo_url}
                  alt={member.stage_name ?? member.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span>{(member.stage_name ?? member.name).charAt(0).toUpperCase()}</span>
              )}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">
              {member?.stage_name ?? member?.name ?? 'Member'}
            </h1>
            <p className="text-muted-foreground text-sm">Riwayat Cheki</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="rounded-xl border border-border bg-card px-6 py-4">
        <p className="text-sm text-muted-foreground">
          Total cheki terverifikasi:{' '}
          <strong className="text-foreground">{totalVerified} set</strong>
          <span className="ml-3 text-muted-foreground/70">({orders.length} transaksi)</span>
        </p>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Memuat data...</p>
        ) : orders.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Belum ada order cheki untuk member ini.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Kode</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Fan</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Form / Event</th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">Qty</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Tanggal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(orders as MemberChekiOrder[]).map((order) => {
                const statusCfg = STATUS_LABELS[order.status] ?? {
                  label: order.status,
                  className: 'bg-gray-100 text-gray-700',
                }
                return (
                  <tr key={order.id} className="bg-card hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                      {order.booking_code}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{order.fan_name}</p>
                      <p className="text-xs text-muted-foreground">{order.fan_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium">{order.cheki_forms?.title ?? '—'}</p>
                      {order.cheki_forms?.events && (
                        <p className="text-xs text-muted-foreground">
                          {order.cheki_forms.events.title}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{order.quantity ?? 1}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.className}`}
                      >
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(order.created_at, { dateStyle: 'short' })}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
